import React, { ReactNode, useEffect, useState } from "react";
import PuiTypography from "../../components/elements/PuiTypography";
import styles from "./StatisticsManagementIndex.module.css";
import {
    Box,
    CircularProgress,
    createStyles,
    makeStyles,
    Tab,
    Tabs,
    TextField,
    withStyles,
} from "@material-ui/core";
import { format } from "date-fns";
import DownloadIcon from "../../assets/icon/DownloadIcon";
import { useDispatch, useSelector } from "react-redux";
import {
    GetDocumentPopularRanking,
    GetDocumentStatistics,
    GetPopularRankingStarted,
    GetStatisticsStarted,
} from "../../store/actions/StatisticsActions";
import { AppState } from "../../root/RootReducer";
import { Line, Doughnut } from "react-chartjs-2";
import { Chart, registerables } from "chart.js";
import { PopularVideoList, PopularTagList } from "../../models/statistics";
import ChartDataLabels from "chartjs-plugin-datalabels";
import PuiSelect from "../../components/elements/PuiSelect";
import { CONTENT_TYPE_LIST } from "../../utils/Contants";
import { useInitText } from "../../components/function/useInitText";
import getAxiosInstance from "../../common/mhiAxios";
import { t } from "i18next";

/**
|--------------------------------------------------
| 統計管理画面
|--------------------------------------------------
*/
//タブ値設定
const TABVIEWCOUNT: number = 0;
const TABPLAYTIME: number = 1;
const TABUSERCOUNT: number = 2;
const TABSLIDEUSERCOUNT: number = 1;

//グラフLine使う為のChartをregisterする必要
Chart.register(...registerables);

//グラフ表示用
interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

// 全体Taps用
interface StatisticsTabsProps {
    value: number;
    className: string;
    onChange: (event: React.ChangeEvent<{}>, newValue: number) => void;
}

// 各Tap用
interface StatisticsTabProps {
    label: ReactNode;
}

// グラフ表示パンネル
function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`tabpanel-${index}`}
            aria-labelledby={`tab-${index}`}
            className={styles.graphContainer}
            {...other}
        >
            {value === index && <Box p={3}>{children}</Box>}
        </div>
    );
}

// 全体スタイル
const useStyles = makeStyles(() =>
    createStyles({
        dateField: {
            width: "170px",
            margin: "0 10px",
            "& .MuiInputBase-root": {
                color: "white",
                "& .MuiSelect-iconOutlined": {
                    color: "var(--loading-bg)",
                },
            },
        },
        dateSelect: {
            border: "var(--gray-38)",
            borderRadius: "5px",
            fontSize: "14px",
            background: "#FFFFFF0D",
            fontFamily: "noto-sans-cjk-jp,sans-serif !important",
            "& .MuiSelect-icon": {
                color: "white",
            },
            "& .MuiSelect-selectMenu": {
                color: "white",
            },
        },
    })
);

// 日付項目スタイル
const DateTextField = withStyles({
    root: {
        "& .MuiInput-underline:before": {
            borderBottomColor: "var(--white)",
        },
        "& .MuiInput-underline:after": {
            borderBottomColor: "var(--white)",
        },
        "& .MuiInput-icon": {
            color: "var(--white)",
        },
    },
})(TextField);

// Tabスタイル
const StatisticsTabs = withStyles({
    root: {
        border: "1px solid var(--gray-61)",
    },
    indicator: {
        display: "flex",
        justifyContent: "center",
        backgroundColor: "transparent",
        "& > span": {
            width: "100%",
        },
    },
})((props: StatisticsTabsProps) => (
    <Tabs
        {...props}
        variant="fullWidth"
        scrollButtons="off"
        textColor="secondary"
        TabIndicatorProps={{ children: <span /> }}
    />
));

//Tab Bodyのスタイル
const StatisticsTab = withStyles(() =>
    createStyles({
        root: {
            textTransform: "none",
            minWidth: 50,
            fontFamily: "noto-sans-cjk-jp,sans-serif !important",
            border: "1px solid var(--gray-61)",
            backgroundColor: "var(--textfield-bg)",
            "&:hover": {
                backgroundColor: "#35393F",
            },
            "&$selected": {
                backgroundColor: "#35393F",
            },
            "&:focus": {
                backgroundColor: "#35393F",
            },
        },
        selected: {},
    })
)((props: StatisticsTabProps) => <Tab disableRipple {...props} />);

// Tap index設定
function a11yProps(index: any) {
    return {
        id: `tab-${index}`,
        "aria-controls": `tabpanel-${index}`,
    };
}
export default function StatisticsManagementIndex() {
    /* 変数の設定 */
    const classes = useStyles();
    const dispatch = useDispatch();
    //開始日は当月の1日に設定する
    const [startDate, setStartDate] = useState<string>(format(new Date().setDate(1), "yyyy-MM-dd"));
    //終了日は当日（現在の日付）に設定する
    const [endDate, setEndDate] = useState<string>(format(new Date(), "yyyy-MM-dd"));
    //初期表示は視聴回数タブを表示される
    const [tabIndex, setTabIndex] = useState<number>(0);
    //視聴回数用
    const [viewCountList, setViewCountList] = useState<number[]>([]);
    const [viewCountLabel, setViewCountLabel] = useState<string[]>([]);
    //総再生時間用
    const [playTimeList, setPlayTimeList] = useState<number[]>([]);
    const [playTimeLabel, setPlayTimeLabel] = useState<string[]>([]);
    //視聴人数用
    const [userCountList, setUserCountList] = useState<number[]>([]);
    const [userCountLabel, setUserCountLabel] = useState<string[]>([]);
    //人気再生時間用
    const [rankGraphData, setRankGraphData] = useState<number[]>([]);
    const [rankGraphLabel, setRankGraphLabel] = useState<string[]>([]);
    // コンテンツタイプ
    const [contentType, setContentType] = useInitText(CONTENT_TYPE_LIST[0]);
    // 人気コンテンツリスト
    const [popularContentList, setPopularContentList] = useState<PopularVideoList[]>([]);

    // プロジェクトパラメータ
    const { statistics, popularRanking, isLoading } = useSelector((state: AppState) => ({
        statistics: state.statistics.statistics,
        popularRanking: state.statistics.popularRanking,
        isLoading: state.statistics.isLoading,
    }));

    /* 関数の設定 */

    // 検索期間（開始日）変更
    const handleChangeStartDate = (event: React.ChangeEvent<HTMLInputElement>) => {
        setStartDate(event.target.value.split("/").join("-"));
    };

    // 検索期間（終了日）変更
    const handleChangeEndDate = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEndDate(event.target.value.split("/").join("-"));
    };

    // タブ切り替え
    const handleChangeTab = (event: React.ChangeEvent<{}>, index: number) => {
        setTabIndex(index);
    };

    // 統計情報・人気ランキング情報取得
    useEffect(() => {
        if (contentType === "Video") {
            //統計情報取得
            dispatch(GetStatisticsStarted(startDate, endDate));
            //人気ランキング情報取得
            dispatch(GetPopularRankingStarted(startDate, endDate));
        } else if (contentType === "Slide") {
            //統計情報取得
            dispatch(GetDocumentStatistics(startDate, endDate));
            //人気ランキング情報取得
            dispatch(GetDocumentPopularRanking(startDate, endDate));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate, contentType]);

    useEffect(() => {
        // 視聴回数
        let viewCountDatas: number[] = [];
        let viewCountLabels: string[] = [];
        statistics &&
            statistics.viewCountList.map((item) => {
                viewCountDatas.push(item.statisticsCount);
                //yyyy-MM-DD ⇒ yyyy/MM/DD 変換
                viewCountLabels.push(String(item.statisticsDate).split("-").join("/"));
                return null;
            });
        //視聴回数タブヘッダーに表示する為
        setViewCountList(viewCountDatas);
        //視聴回数タブBodyに表示する為
        setViewCountLabel(viewCountLabels);

        //総再生時間(Videoのみ)
        let timePlayDatas: number[] = [];
        let timePlayLabels: string[] = [];
        statistics &&
            statistics.playTimeList.map((item) => {
                //ミリ秒から時間変換（ミリ秒は0より多い AND　変換値は0の場合、定値0.01で表示する）
                timePlayDatas.push(
                    Math.round((item.statisticsCount / 3600000) * 100) / 100 === 0 &&
                        item.statisticsCount > 0
                        ? 0.01
                        : Math.round((item.statisticsCount / 3600000) * 100) / 100
                );
                //yyyy-MM-DD ⇒ yyyy/MM/DD 変換
                timePlayLabels.push(String(item.statisticsDate).split("-").join("/"));
                return null;
            });
        //総再生時間回数タブヘッダーに表示する為
        setPlayTimeList(timePlayDatas);
        //総再生時間タブBodyに表示する為
        setPlayTimeLabel(timePlayLabels);

        // 視聴人数
        let userCountDatas: number[] = [];
        let userCountLabels: string[] = [];
        statistics &&
            statistics.userCountList.map((item) => {
                userCountDatas.push(item.statisticsCount);
                //yyyy-MM-DD ⇒ yyyy/MM/DD 変換
                userCountLabels.push(String(item.statisticsDate).split("-").join("/"));
                return null;
            });
        //視聴人数タブヘッダーに表示する為
        setUserCountList(userCountDatas);
        //視聴人数タブBodyに表示する為
        setUserCountLabel(userCountLabels);

        // 人気再生時間
        let doughnutDataList: number[] = [];
        let doughnutLabelList: string[] = [];
        popularRanking &&
            popularRanking.popularPeriodList.map((item) => {
                doughnutDataList.push(item.count);
                doughnutLabelList.push(item.period);
                return null;
            });
        setRankGraphData(doughnutDataList);
        setRankGraphLabel(doughnutLabelList);
    }, [setViewCountList, statistics, popularRanking]);

    //視聴回数グラフ表示定義
    const viewCountData = {
        labels: viewCountLabel,
        datasets: [
            {
                label: "count",
                data: viewCountList,
                fill: true,
                backgroundColor: "rgba(255, 0, 0, 0.1)",
                borderColor: "rgba(255, 0, 0, 0.4)",
            },
        ],
    };

    //総再生時間(Videoのみ)グラフ表示定義
    const playTimeData = {
        labels: playTimeLabel,
        datasets: [
            {
                label: "count",
                data: playTimeList,
                fill: true,
                backgroundColor: "rgba(0, 255, 0, 0.1)",
                borderColor: "rgba(0, 255, 0, 0.4)",
            },
        ],
    };

    //視聴人数(ユニック))グラフ表示定義
    const userCountData = {
        labels: userCountLabel,
        datasets: [
            {
                label: "count",
                data: userCountList,
                fill: true,
                backgroundColor: "rgba(0, 0, 255, 0.1)",
                borderColor: "rgba(0, 0, 255, 0.4)",
            },
        ],
    };

    //linear線を作る
    var delayed: boolean;
    const lineOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            y: {
                beginAtZero: true,
                min: 0,
                stacked: true,
            },
            x: {
                stacked: true,
            },
        },
        plugins: {
            legend: { display: false },
        },
        animation: {
            onComplete: () => {
                delayed = true;
            },
            delay: (context: any) => {
                let delay = 0;
                if (context.type === "data" && context.mode === "default" && !delayed) {
                    delay = context.dataIndex * 200 + context.datasetIndex * 100;
                }
                return delay;
            },
        },
    };

    //よく視聴される時間帯グラフの設定
    const data = {
        labels: rankGraphLabel,
        datasets: [
            {
                label: "Popular Play Time Dataset",
                data: rankGraphData,
                backgroundColor: [
                    "rgba(255, 99, 132, 0.5)",
                    "rgba(54, 162, 235, 0.5)",
                    "rgba(255, 206, 86, 0.5)",
                    "rgba(75, 192, 192, 0.5)",
                    "rgba(153, 102, 255, 0.5)",
                    "rgba(192, 192, 192, 0.5)",
                ],
                hoverOffset: 4,
                datalabels: {
                    labels: {
                        name: {
                            align: "bottom",
                            color: "white",
                            font: { size: 14, weight: "bold" },
                            formatter: function (value: any, ctx: any) {
                                return ctx.chart.data.labels[ctx.dataIndex];
                            },
                            padding: 1,
                        },
                    },
                },
            },
        ],
    };

    //よく視聴される時間帯ラベルの設定
    const timeRankinOptions = {
        plugins: {
            datalabels: {
                anchor: function (context: any) {
                    return Math.round(
                        (Number(context.chart.data.datasets[0].data[context.dataIndex]) /
                            rankGraphData.reduce((sum, item) => sum + Number(item), 0)) *
                            100
                    ) < 5
                        ? "end"
                        : "center";
                },
            },
            tooltip: {
                enabled: true,
                callbacks: {
                    label: function (ctx: any) {
                        return (
                            ctx.chart.data.datasets[0].data[ctx.dataIndex] +
                            t("回 - ") +
                            Math.round(
                                (Number(ctx.chart.data.datasets[0].data[ctx.dataIndex]) /
                                    rankGraphData.reduce((sum, item) => sum + Number(item), 0)) *
                                    100
                            ) +
                            "%"
                        );
                    },
                },
            },
        },
        animation: true,
        cutout: 65,
    };

    // 選択したコンテンツタイプ変更
    const handleChangeType = (type: string) => {
        setContentType(type);
        if (type === "Slide" && tabIndex === 1) {
            setTabIndex(0);
        }
    };

    // CSVダウンロード処理
    const handleClickCSVDownload = () => {
        const exportUrl = contentType === "Video" ? "exportStatistics" : "exportDocumentStatistics";
        const currentAccessToken = localStorage.getItem("cineonhub_access_token");
        getAxiosInstance()
            .get(
                process.env.REACT_APP_API_ENDPOINT +
                    "/api/" +
                    exportUrl +
                    "?startDate=" +
                    startDate +
                    "&endDate=" +
                    endDate,
                {
                    headers: {
                        Authorization: "Bearer " + currentAccessToken,
                        responseType: "blob",
                    },
                }
            )
            .then((res) => {
                let bom = new Uint8Array([0xef, 0xbb, 0xbf]);
                let blob = new Blob([bom, res.data], { type: "text/csv" });
                let url = String([URL.createObjectURL(blob)]);
                // リンクを作成してダウンロードする。
                let link = document.createElement("a");
                link.href = url;
                const sDate = format(new Date(startDate), "yyyyMMdd");
                const eDate = format(new Date(endDate), "yyyyMMdd");
                link.download = contentType + "StatisticsResult_" + sDate + "-" + eDate + ".csv";
                link.click();
            });
    };

    // 人気コンテンツ設定
    useEffect(() => {
        if (popularRanking) {
            if (contentType === "Video" && popularRanking?.popularVideoList !== undefined) {
                setPopularContentList(popularRanking.popularVideoList);
            } else if (
                contentType === "Slide" &&
                popularRanking?.popularDocumentList !== undefined
            ) {
                setPopularContentList(popularRanking.popularDocumentList);
            }
        }
        // eslint-disable-next-line
    }, [popularRanking, contentType]);

    return (
        // ルートパンネル
        <div className={styles.root}>
            {/* データ取得中 */}
            {isLoading && (
                <div className={styles.noContent}>
                    <CircularProgress size={60} style={{ color: "var(--loading-bg)" }} />
                </div>
            )}
            {/* 条件パンネル */}
            <div className={styles.conditionArea}>
                {/*期間入力エリア*/}
                <div className={styles.conditions}>
                    <PuiTypography size={20} color={"white"}>
                        {t("期間")}
                    </PuiTypography>
                    <DateTextField
                        id="startDate"
                        type="date"
                        defaultValue={startDate}
                        className={classes.dateField}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        onChange={handleChangeStartDate}
                    />
                    <PuiTypography size={20} color={"white"}>
                        ～
                    </PuiTypography>
                    <DateTextField
                        id="endDate"
                        type="date"
                        defaultValue={endDate}
                        className={classes.dateField}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        onChange={handleChangeEndDate}
                    />
                    <div style={{ width: "150px", marginLeft: "10px" }}>
                        <PuiSelect
                            id="period"
                            childrens={CONTENT_TYPE_LIST}
                            defaultValue={CONTENT_TYPE_LIST[0]}
                            onChange={handleChangeType}
                        />
                    </div>
                </div>
                {/* ダウンロードエリア */}
                <div className={styles.downloadLink} onClick={handleClickCSVDownload}>
                    <DownloadIcon size={18} />
                    <PuiTypography size={20} color={"white"}>
                        {t("CSVダウンロード")}
                    </PuiTypography>
                </div>
            </div>
            {/* グラフパンネル */}
            <div className={styles.graphArea}>
                {/* Tapsヘッダー */}
                <StatisticsTabs
                    value={tabIndex}
                    onChange={handleChangeTab}
                    className={styles.statisticstabs}
                    aria-label="Statistics Tabs"
                >
                    <StatisticsTab
                        label={
                            <PuiTypography
                                size={20}
                                bold={tabIndex === 0 ? true : false}
                                color={"secondary-font"}
                            >
                                {contentType === "Video" ? t("視聴回数") : t("閲覧回数")}
                                <br />
                                {`${viewCountList.reduce((sum, item) => sum + Number(item), 0)}回`}
                            </PuiTypography>
                        }
                        {...a11yProps(0)}
                    />

                    {contentType === "Video" && (
                        <StatisticsTab
                            label={
                                <PuiTypography
                                    size={20}
                                    bold={tabIndex === 1 ? true : false}
                                    color={"secondary-font"}
                                >
                                    {t("総再生時間")}
                                    <br />
                                    {`${
                                        playTimeList.reduce(
                                            (sum, item) =>
                                                Math.round(Number(sum) + Number(item) * 100),
                                            0
                                        ) / 100
                                    }`}
                                    {t("時間")}
                                </PuiTypography>
                            }
                            {...a11yProps(1)}
                        />
                    )}

                    <StatisticsTab
                        label={
                            <PuiTypography
                                size={20}
                                bold={
                                    contentType === "Video"
                                        ? tabIndex === 2
                                        : tabIndex === 1
                                        ? true
                                        : false
                                }
                                color={"secondary-font"}
                            >
                                {contentType === "Video"
                                    ? t("視聴者数（ユニーク）")
                                    : t("閲覧者数（ユニーク）")}
                                <br />
                                {statistics && `${statistics.userCount}人`}
                            </PuiTypography>
                        }
                        {...a11yProps(1)}
                    />
                </StatisticsTabs>
                {/* Taps bodyエリア */}
                <TabPanel value={tabIndex} index={TABVIEWCOUNT}>
                    {/* @ts-ignore */}
                    <Line data={viewCountData} height={300} options={lineOptions} />
                </TabPanel>

                {contentType === "Video" && (
                    <TabPanel value={tabIndex} index={TABPLAYTIME}>
                        {/* @ts-ignore */}
                        <Line data={playTimeData} height={300} options={lineOptions} />
                    </TabPanel>
                )}

                <TabPanel
                    value={tabIndex}
                    index={contentType === "Video" ? TABUSERCOUNT : TABSLIDEUSERCOUNT}
                >
                    {/* @ts-ignore */}
                    <Line data={userCountData} height={300} options={lineOptions} />
                </TabPanel>
            </div>
            {/* 人気ランキングエリア */}
            <div className={styles.rankingArea}>
                {/* 人気コンテンツエリア */}
                <div className={styles.popularContentsArea}>
                    <PuiTypography size={20} bold={true} color={"white"}>
                        {t("人気のコンテンツ")}
                    </PuiTypography>
                    <div className={styles.popularVideoList}>
                        {popularContentList.slice(0, 10).map((content: PopularVideoList, index) => (
                            <div className={styles.popularContent} key={index}>
                                <img
                                    src={content.imageUrl}
                                    className={styles.popularContentImg}
                                    alt={content.title}
                                    onError={(e) =>
                                        (e.currentTarget.src =
                                            require("../../assets/image/NoImage.jpg").default)
                                    }
                                />
                                <div className={styles.popularContentTitle}>
                                    <PuiTypography size={14} color={"white"}>
                                        {content.title}
                                    </PuiTypography>
                                </div>
                                <div className={styles.popularContentCount}>
                                    <PuiTypography size={14} color={"white"}>
                                        {`${content.count}回`}
                                    </PuiTypography>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                {/* 人気タグエリア */}
                <div className={styles.popularTagArea}>
                    <PuiTypography size={20} bold={true} color={"white"}>
                        {t("人気のタグ")}
                    </PuiTypography>
                    <div className={styles.popularTagList}>
                        {popularRanking !== undefined &&
                            popularRanking.popularTagList
                                .slice(0, 10)
                                .map((tag: PopularTagList, index) => (
                                    <div className={styles.popularTag} key={index}>
                                        <div className={styles.popularTagName}>
                                            <PuiTypography size={14} color={"white"}>
                                                {tag.tagName}
                                            </PuiTypography>
                                        </div>
                                        <div className={styles.popularTagCount}>
                                            <PuiTypography size={14} color={"white"}>
                                                {`${tag.count}`}
                                                {t("回")}
                                            </PuiTypography>
                                        </div>
                                    </div>
                                ))}
                    </div>
                </div>
                {/* 人気再生時間 */}
                <div className={styles.popularTimeArea}>
                    <PuiTypography size={20} bold={true} color={"white"}>
                        {t("よく視聴・閲覧される時間帯")}
                    </PuiTypography>
                    {/* @ts-ignore */}
                    <Doughnut data={data} plugins={[ChartDataLabels]} options={timeRankinOptions} />
                </div>
            </div>
        </div>
    );
}
